/**
 * Drawer — right-side overlay panel. Replaces most modals.
 *
 * When the user wants to review ONE item (invoice, room, contract) while
 * keeping the list context, drawer is correct. Modal is only for destructive
 * confirms or rare full-attention flows.
 *
 *   <Drawer open={open} onClose={...} title="INV-2026-0512" width={420}>
 *     <DrawerBody>...</DrawerBody>
 *     <DrawerFooter>
 *       <Button kind="secondary">Tải PDF</Button>
 *       <Button kind="primary">Gửi biên nhận</Button>
 *     </DrawerFooter>
 *   </Drawer>
 *
 * Esc closes. Backdrop click closes. Body scroll is locked while open.
 * Focus traps to the panel; restores on close.
 */

import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from './icons';
import { IconButton } from './Button';

export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  width = 380,
  side = 'right',
  closeOnBackdrop = true,
  className = '',
  children,
}) {
  const { t } = useTranslation('common');
  const panelRef = useRef(null);
  const lastFocused = useRef(null);

  // Lock body scroll, save last-focused element
  useEffect(() => {
    if (!open) return;
    lastFocused.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Focus first focusable in panel
    requestAnimationFrame(() => {
      const focusable = panelRef.current?.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      focusable?.focus();
    });
    return () => {
      document.body.style.overflow = prevOverflow;
      lastFocused.current?.focus?.();
    };
  }, [open]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby="drawer-title">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-stone-900/30 animate-fade-in"
        onClick={closeOnBackdrop ? onClose : undefined}
      />
      {/* Panel */}
      <aside
        ref={panelRef}
        className={[
          'absolute top-0 bottom-0 bg-white shadow-drawer animate-slide-in-r',
          'flex flex-col',
          side === 'right' ? 'right-0 border-l border-stone-200' : 'left-0 border-r border-stone-200',
          className,
        ].join(' ')}
        style={{ width, maxWidth: '100vw' }}
      >
        {(title || subtitle) && (
          <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-stone-200 shrink-0">
            <div className="min-w-0">
              {title && (
                <h2 id="drawer-title" className="text-base font-semibold -tracking-tight text-stone-900 truncate-line">
                  {title}
                </h2>
              )}
              {subtitle && <p className="mt-0.5 text-xs text-stone-500">{subtitle}</p>}
            </div>
            <IconButton kind="ghost" size="sm" icon={<X />} onClick={onClose} aria-label={t('action.close')} />
          </header>
        )}
        {children}
      </aside>
    </div>
  );
}

export function DrawerBody({ className = '', children, ...rest }) {
  return (
    <div className={['flex-1 overflow-y-auto scrollbar-thin', className].join(' ')} {...rest}>
      {children}
    </div>
  );
}

export function DrawerSection({ title, action, className = '', children }) {
  return (
    <section className={['px-5 py-4 border-b border-stone-100 last:border-b-0', className].join(' ')}>
      {(title || action) && (
        <div className="flex items-center justify-between mb-3">
          {title && (
            <h3 className="text-2xs font-semibold uppercase tracking-wider text-stone-500">{title}</h3>
          )}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function DrawerFooter({ className = '', children }) {
  return (
    <footer className={['flex items-center gap-2 px-5 py-3 border-t border-stone-200 shrink-0', className].join(' ')}>
      {children}
    </footer>
  );
}

// ─── Key-value list — used heavily in drawer bodies ─────────────────────────
// Accepts either `{ label, value, mono }` objects or `[label, value]` tuples.
export function KVList({ items, className = '' }) {
  return (
    <dl className={['divide-y divide-stone-100', className].join(' ')}>
      {items.map((it, i) => {
        const label = Array.isArray(it) ? it[0] : it.label;
        const value = Array.isArray(it) ? it[1] : it.value;
        const mono  = Array.isArray(it) ? false  : it.mono;
        return (
          <div key={i} className="flex items-baseline justify-between gap-3 py-2">
            <dt className="text-xs text-stone-500">{label}</dt>
            <dd className={['text-sm text-stone-900 font-medium text-right', mono && 'mono'].filter(Boolean).join(' ')}>
              {value}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
